import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Package, ShoppingCart, Shirt, Car, Pill, Laptop, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Package,
  ShoppingCart,
  Shirt,
  Car,
  Pill,
  Laptop
};

const IndustrySectors = () => {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const itemsPerView = 4;

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(`${API}/industries`);
      if (response.data.success) {
        setIndustries(response.data.industries);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (startIndex + itemsPerView < industries.length) {
      setStartIndex(startIndex + 1);
    }
  };

  const handlePrev = () => {
    if (startIndex > 0) {
      setStartIndex(startIndex - 1);
    }
  };

  const visibleSectors = industries.slice(startIndex, startIndex + itemsPerView);

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0A1F3B] mb-12 text-center">
            Industry sectors
          </h2>
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#00ACC1]" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <h2 className="text-3xl lg:text-4xl font-bold text-[#0A1F3B] mb-12 text-center">
          Industry sectors
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
          Regardless of your industry, your commodity, or your key markets, Supreme Global Freight offers global and local logistics solutions that enable small and large businesses to grow.
        </p>

        <div className="relative">
          {/* Navigation Buttons */}
          <Button
            variant="outline"
            size="icon"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 rounded-full border-2 bg-white hover:bg-gray-50"
            onClick={handlePrev}
            disabled={startIndex === 0}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 rounded-full border-2 bg-white hover:bg-gray-50"
            onClick={handleNext}
            disabled={startIndex + itemsPerView >= industries.length}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>

          {/* Sectors Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 px-8">
            {visibleSectors.map((sector) => {
              const IconComponent = iconMap[sector.icon] || Package;
              return (
                <div
                  key={sector.id}
                  className="flex flex-col items-center justify-center p-8 rounded-lg hover:bg-gray-50 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-32 h-32 flex items-center justify-center mb-4 rounded-full bg-gray-100 group-hover:bg-[#00ACC1]/10 transition-colors duration-300">
                    <IconComponent className="w-16 h-16 text-[#0A1F3B] group-hover:text-[#00ACC1] transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0A1F3B] group-hover:text-[#00ACC1] transition-colors">
                    {sector.name}
                  </h3>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustrySectors;